import { useEffect, useMemo, useState } from 'react';
import { AgeGateOverlay } from '@hypneuronerd/age-gate';
import { storage } from '@hypneuronerd/storage';
import { ReturnLink } from './components/ReturnLink';
import { Welcome } from './components/Welcome';
import { Question } from './components/Question';
import { Result } from './components/Result';
import { QUESTIONS } from './data/questions';
import { computeScores } from './lib/scoring';
import { decodeHash } from './lib/encoding';
import { suggestName } from './lib/names';
import type { Answers, Likert, SavedConstellation, Scores } from './data/types';
import styles from './App.module.css';

type Phase = 'welcome' | 'question' | 'result';

const SAVED_KEY = 'archetype-mirror:saved';
const IN_PROGRESS_KEY = 'archetype-mirror:in-progress';

export function App() {
  // Detect if there's a shared constellation in the URL hash on load
  const sharedFromUrl = useMemo(() => {
    if (typeof window === 'undefined' || !window.location.hash) return null;
    return decodeHash(window.location.hash);
  }, []);

  const [phase, setPhase] = useState<Phase>(sharedFromUrl ? 'result' : 'welcome');
  const [answers, setAnswers] = useState<Answers>(() =>
    storage.get<Answers>(IN_PROGRESS_KEY, {})
  );
  const [index, setIndex] = useState(0);
  const [saved, setSaved] = useState<SavedConstellation | null>(() =>
    storage.get<SavedConstellation | null>(SAVED_KEY, null)
  );

  // Resume from URL hash when present
  const [viewScores, setViewScores] = useState<Scores | null>(() =>
    sharedFromUrl ? sharedFromUrl.scores : null
  );
  const [viewName, setViewName] = useState<string>(
    () => sharedFromUrl?.name ?? ''
  );

  // Persist in-progress answers
  useEffect(() => {
    if (phase === 'question') storage.set(IN_PROGRESS_KEY, answers);
  }, [answers, phase]);

  // Hydrate viewName if scores are present but name isn't set
  useEffect(() => {
    if (viewScores && !viewName) {
      setViewName(suggestName(viewScores));
    }
  }, [viewScores, viewName]);

  const question = QUESTIONS[index];

  function begin() {
    setAnswers({});
    setIndex(0);
    setViewScores(null);
    setViewName('');
    storage.remove(IN_PROGRESS_KEY);
    setPhase('question');
  }

  function viewSaved() {
    if (saved) {
      setViewScores(saved.scores);
      setViewName(saved.name);
      setPhase('result');
    }
  }

  function answer(value: Likert) {
    if (!question) return;
    const next = { ...answers, [question.id]: value };
    setAnswers(next);
    // Small delay so the user sees their selection, then advance
    window.setTimeout(() => {
      if (index + 1 < QUESTIONS.length) {
        setIndex(index + 1);
      } else {
        const scores = computeScores(next);
        setViewScores(scores);
        setViewName(suggestName(scores));
        storage.remove(IN_PROGRESS_KEY);
        setPhase('result');
      }
    }, 220);
  }

  function back() {
    if (index > 0) setIndex(index - 1);
  }

  function saveCurrent() {
    if (!viewScores) return;
    const record: SavedConstellation = {
      scores: viewScores,
      name: viewName || suggestName(viewScores),
      createdAt: new Date().toISOString(),
      version: 1,
    };
    storage.set(SAVED_KEY, record);
    setSaved(record);
  }

  function retake() {
    setPhase('welcome');
    setViewScores(null);
    setViewName('');
  }

  const isSavedMatch =
    saved && viewScores && JSON.stringify(saved.scores) === JSON.stringify(viewScores);

  return (
    <>
      <AgeGateOverlay />
      <header className={styles.topNav}>
        <ReturnLink />
      </header>

      <main className={styles.stage}>
        {phase === 'welcome' && (
          <Welcome saved={saved} onBegin={begin} onView={viewSaved} />
        )}

        {phase === 'question' && question && (
          <Question
            question={question}
            index={index}
            total={QUESTIONS.length}
            value={answers[question.id]}
            onAnswer={answer}
            onBack={back}
            canBack={index > 0}
          />
        )}

        {phase === 'result' && viewScores && (
          <Result
            scores={viewScores}
            name={viewName}
            onNameChange={setViewName}
            saved={Boolean(isSavedMatch)}
            onSave={saveCurrent}
            onRetake={retake}
          />
        )}
      </main>

      <footer className={styles.footer}>
        <p>
          not a verdict. a self-portrait at this moment. revisit when something
          shifts in you.
        </p>
      </footer>
    </>
  );
}
